import React, { useEffect, useState } from "react";
import { Sparkles, TrendingUp, ArrowUpRight, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { dashApi } from "../api";
import { formatRelative } from "../utils";

// Human-readable labels for the beacon source keys used by the
// referrer/utm detector on the frontend and the FastAPI webhook.
const SOURCE_LABEL = {
  perplexity: "Perplexity",
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  copilot: "Copilot",
  you: "You.com",
  phind: "Phind",
  bing_chat: "Bing Chat",
  google_ai_overview: "Google AI Overview",
};

const sourceLabel = (s) => SOURCE_LABEL[s] || s?.replace(/_/g, " ") || "AI";

/**
 * "AI-referred leads" panel on the dashboard Overview page.
 *
 * Renders three panes:
 *   1. Headline totals (visits, conversions, conversion rate)
 *   2. Per-source breakdown
 *   3. Live feed — most recent AI landings
 *
 * Silently hides itself if the summary endpoint fails (this is a
 * nice-to-have widget, not a critical control — the dashboard should
 * never break because AI attribution had a hiccup).
 */
export default function AiBeaconWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: resp } = await dashApi.get(
          "/ai-attribution/summary",
          { params: { days: 30 } },
        );
        if (alive) setData(resp);
      } catch {
        if (alive) setErrored(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading || errored || !data) return null;

  const totals = data.totals || {};
  const isEmpty = !totals.visits;

  return (
    <Card
      className="p-5 border-violet-100 bg-gradient-to-br from-violet-50/50 to-white"
      data-testid="ai-beacon-widget"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            AI-referred leads
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Visits and conversions from Perplexity, ChatGPT, Claude, Gemini and
            other AI answer engines · last {data.window_days} days
          </p>
        </div>
      </div>

      {isEmpty ? (
        <div className="rounded-md border border-dashed border-slate-200 p-6 text-center" data-testid="ai-beacon-empty">
          <Sparkles className="h-6 w-6 mx-auto text-slate-300" />
          <p className="text-sm text-slate-500 mt-2">
            No AI-referred visitors yet — you're all set to catch them the
            moment they arrive.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Beacon is live on every page; the panel populates automatically.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-md border border-violet-100 bg-white p-3" data-testid="ai-beacon-visits">
              <div className="text-[11px] uppercase tracking-wider text-violet-700 font-medium">Visits</div>
              <div className="text-2xl font-heading font-bold text-slate-900 mt-1">{totals.visits}</div>
            </div>
            <div className="rounded-md border border-emerald-100 bg-white p-3" data-testid="ai-beacon-conversions">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-medium">Conversions</div>
              <div className="text-2xl font-heading font-bold text-slate-900 mt-1">{totals.conversions}</div>
            </div>
            <div className="rounded-md border border-blue-100 bg-white p-3" data-testid="ai-beacon-rate">
              <div className="text-[11px] uppercase tracking-wider text-blue-700 font-medium inline-flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Conv. rate
              </div>
              <div className="text-2xl font-heading font-bold text-slate-900 mt-1">
                {Math.round((totals.conversion_rate || 0) * 1000) / 10}%
              </div>
            </div>
          </div>

          {data.by_source && data.by_source.length > 0 && (
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">By source</div>
              <ul className="space-y-1.5">
                {data.by_source.map((row) => (
                  <li
                    key={row.source}
                    className="flex items-center justify-between text-sm"
                    data-testid={`ai-beacon-source-${row.source}`}
                  >
                    <span className="font-medium text-slate-800">{sourceLabel(row.source)}</span>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{row.visits} visit{row.visits === 1 ? "" : "s"}</span>
                      <span className="text-emerald-700 font-medium">
                        {row.conversions} conv
                      </span>
                      <span className="text-slate-400 w-10 text-right">
                        {Math.round((row.conversion_rate || 0) * 1000) / 10}%
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.recent && data.recent.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                Recent activity
              </div>
              <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                {data.recent.slice(0, 8).map((r, i) => (
                  <li
                    key={`${r.session_id || "s"}-${r.created_at || i}`}
                    className="flex items-center justify-between gap-2 text-xs text-slate-600 py-1 border-b border-slate-100 last:border-b-0"
                    data-testid={`ai-beacon-recent-${i}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={
                        "shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium " +
                        (r.event_type === "convert"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-violet-100 text-violet-800")
                      }>
                        {r.event_type === "convert" ? <ArrowUpRight className="h-3 w-3 mr-0.5" /> : null}
                        {sourceLabel(r.source)}
                      </span>
                      <span className="truncate font-mono text-[10px] text-slate-500" title={r.landing_page}>
                        {r.landing_page || "/"}
                      </span>
                    </div>
                    <span className="shrink-0 text-slate-400">{formatRelative(r.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
